/**
 * admin/makua/AdminGuests.js — Retreats → Guests (V2.1 sidebar).
 *
 * Everyone who is, or was, on a reservation: one row per person, with
 * their next arrival, their stays and how to reach them. Opening a guest
 * opens their latest reservation; their full list is one click away in
 * Reservations. Contact details only, nothing medical.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "../AdminLayout";
import { reservationsAdminAPI } from "../../services/api";
import ReservationDrawer from "./ReservationDrawer";
import { useLoad, Field, money, fmtDate } from "./ops";
import { useStored, SortTh } from "./kit";
import "./ops.css";

export default function AdminGuests() {
  const [q, setQ] = useStored("guests:q", "");
  const [when, setWhen] = useStored("guests:when", "upcoming");
  const [sort, setSort] = useStored("guests:sort", { key: "arrive", dir: "asc" });
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(null);
  const [debounced, setDebounced] = useState(q);
  useEffect(() => { const t = setTimeout(() => { setDebounced(q); setPage(1); }, 250); return () => clearTimeout(t); }, [q]);
  const { data, raw, loading, error, reload } = useLoad(
    () => reservationsAdminAPI.guests({ q: debounced.trim(), when, sort: sort.key || "", dir: sort.dir, page, limit: 50 }),
    [debounced, when, sort.key, sort.dir, page]);
  const rows = data || [];
  const pg = raw?.pagination;
  const setSortAndPage = (s) => { setSort(s); setPage(1); };

  return (
    <AdminLayout pageTitle="Guests" pageSubtitle="Everyone with a reservation: who's coming, who has stayed, and how to reach them." permission="reservations.view" feature="reservations">
      <div className="ops">
        <div className="ops-bar ops-toolbar">
          <Field label="Search" className="ops-search"><input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, email, phone or MAK-…" /></Field>
          <div className="ops-field"><span>Show</span>
            <div className="ops-seg" role="group" aria-label="Show">
              {[["upcoming", "Coming"], ["stayed", "Have stayed"], ["all", "Everyone"]].map(([k, l]) => (
                <button key={k} type="button" aria-pressed={when === k} onClick={() => { setWhen(k); setPage(1); }}>{l}</button>
              ))}
            </div>
          </div>
          {pg && <span className="ops-count">{pg.total} {pg.total === 1 ? "guest" : "guests"}</span>}
        </div>
        {error && <p className="ops-error">{error}</p>}
        <div className="ops-table-wrap">
          <table className="ops-table">
            <thead><tr>
              <SortTh k="name" sort={sort} setSort={setSortAndPage}>Guest</SortTh>
              <th>Phone</th><th>Country</th>
              <SortTh k="arrive" sort={sort} setSort={setSortAndPage}>Next arrival</SortTh>
              <SortTh k="last" sort={sort} setSort={setSortAndPage}>Last stay ended</SortTh>
              <SortTh k="stays" sort={sort} setSort={setSortAndPage} className="num">Stays</SortTh>
              <SortTh k="value" sort={sort} setSort={setSortAndPage} className="num">Booked as lead</SortTh>
              <th>Latest reservation</th>
            </tr></thead>
            <tbody>
              {loading && !rows.length && <tr><td colSpan={8} className="ops-empty">Loading…</td></tr>}
              {!loading && !rows.length && <tr><td colSpan={8} className="ops-empty">No guests match.</td></tr>}
              {rows.map((g) => (
                <tr key={g.gkey}>
                  <td><b>{g.first_name} {g.last_name}</b>{g.email ? <span className="ops-sub"><a href={`mailto:${g.email}`}>{g.email}</a></span> : <span className="ops-sub">no email</span>}</td>
                  <td>{g.phone ? <a href={`tel:${g.phone.replace(/[^+\d]/g, "")}`}>{g.phone}</a> : "—"}</td>
                  <td>{g.country || "—"}</td>
                  <td>{g.next_arrive ? fmtDate(g.next_arrive) : "—"}</td>
                  <td>{g.last_depart ? fmtDate(g.last_depart) : "—"}</td>
                  <td className="num">{g.stays}{g.reservations > g.stays && <span className="ops-sub">{g.reservations - g.stays} cancelled or lapsed</span>}</td>
                  <td className="num">{g.ever_lead ? money(g.booked_value) : "—"}</td>
                  <td>
                    {g.latest_id ? <button className="ops-row-btn" onClick={() => setOpen(g.latest_id)}>{g.latest_ref}</button> : "—"}
                    {g.email && <span className="ops-sub"><Link to={`/admin/reservations?q=${encodeURIComponent(g.email)}`}>All their reservations</Link></span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pg && pg.totalPages > 1 && (
          <div className="ops-actions" style={{ justifyContent: "center" }}>
            <button className="ops-btn" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button>
            <span className="ops-muted" style={{ alignSelf: "center" }}>Page {page} of {pg.totalPages}</span>
            <button className="ops-btn" disabled={page >= pg.totalPages} onClick={() => setPage(page + 1)}>Next</button>
          </div>
        )}
      </div>
      {open && <ReservationDrawer id={open} onClose={() => setOpen(null)} onChanged={reload} />}
    </AdminLayout>
  );
}
